'use client';

import type { QuickFilterItem } from '@weberus/shared-types';
import { useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Chip } from '@yanolja-rnd/nol-design-system';
import { IcArrowmoreFill, IcFilter } from '@yanolja-rnd/nol-icons';
import { InView } from 'react-intersection-observer';
import { debounce } from 'lodash-es';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  globalFilterAtom,
  globalFilterSelectedAreaKeyAtom,
  originalGlobalFilterAtom,
  quickFilterAtom,
  sessionKeyAtom,
} from '@/app/_shared/store/search-filter';
import { useSyncQuickFilterWithQuery } from '@/app/_components/feature/global-filter-modal/hooks/use-sync-quick-filter-with-query';

interface QuickFilterChipsProps {
  onClickChip: (item: QuickFilterItem, index: number) => void;
  onImpressionChip?: (item: QuickFilterItem, index: number) => void;
}

export const QuickFilterChips = (props: QuickFilterChipsProps) => {
  const { onClickChip, onImpressionChip } = props;
  const [isSwipable, setIsSwipable] = useState(false); // 드래그 가능 여부
  const emblaElementRef = useRef<HTMLDivElement | null>(null);
  const [emblaRef] = useEmblaCarousel({
    dragFree: true,
    active: isSwipable, // 활성화 여부
  });
  const filterContainerRef = useRef<HTMLDivElement | null>(null);

  // store getter
  const quickFilter = useAtomValue(quickFilterAtom);
  const originalGlobalFilter = useAtomValue(originalGlobalFilterAtom);
  const sessionKey = useAtomValue(sessionKeyAtom);

  // store setter
  const setGlobalFilter = useSetAtom(globalFilterAtom);
  const setGlobalFilterSelectedAreaKeyAtom = useSetAtom(globalFilterSelectedAreaKeyAtom);

  const { syncQuickFilterWithQuery } = useSyncQuickFilterWithQuery(sessionKey);

  useEffect(() => {
    const checkSwipable = () => {
      if (!emblaElementRef.current || !filterContainerRef.current) {
        return;
      }

      const { offsetWidth, scrollWidth } = filterContainerRef.current;
      setIsSwipable(scrollWidth > offsetWidth);
    };

    checkSwipable(); // 최초 실행

    const throttledCheckDraggable = debounce(checkSwipable, 200);
    window.addEventListener('resize', throttledCheckDraggable);

    return () => {
      window.removeEventListener('resize', throttledCheckDraggable);
      throttledCheckDraggable.cancel(); // 리소스 정리
    };
  }, [quickFilter?.items]);

  const handleClickChip = (item: QuickFilterItem, index: number) => {
    onClickChip(item, index);
    if (item.type === 'OPEN_GLOBAL_FILTER') {
      // 글로벌 필터 열기
      setGlobalFilter(originalGlobalFilter);
      setGlobalFilterSelectedAreaKeyAtom(item.key);
    } else {
      // 퀵 필터 선택 (url query 동기화)
      syncQuickFilterWithQuery(item);
    }
  };

  return (
    <div
      ref={(node) => {
        emblaElementRef.current = node;
        emblaRef(node);
      }}
      className="w-full overflow-hidden">
      <div ref={filterContainerRef} className="mr-16 flex gap-4 pl-16">
        {quickFilter?.items?.map((item, index) => (
          <InView
            key={index}
            className="flex shrink-0"
            triggerOnce
            onChange={(_inView) => {
              _inView && onImpressionChip?.(item, index);
            }}>
            <Chip
              key={index}
              theme={item.selected ? 'outlined' : 'filled'}
              size="medium"
              className="select-none"
              selected={item.selected}
              leftIcon={!!item.isGlobalFilter && <IcFilter />}
              rightIcon={!item.isGlobalFilter && item.type === 'OPEN_GLOBAL_FILTER' && <IcArrowmoreFill />}
              onClick={() => handleClickChip(item, index)}>
              {item.title}
            </Chip>
          </InView>
        ))}
      </div>
    </div>
  );
};
